// src/components/ChatContainer.js
import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, Paperclip, Send, X, Code2 } from 'lucide-react';
import MessageRenderer from './MessageRenderer';
import TokenDisplay from './TokenDisplay';
import SessionTracker from './SessionTracker'; // still imported for display
import CostConfirmationModal from './CostConfirmationModal';
import './ChatContainer.css';

const ChatContainer = ({ 
  messages, 
  isLoading, 
  onSendMessage, 
  inputMessage, 
  setInputMessage, 
  attachments = [], 
  onFileUpload, 
  onRemoveAttachment, 
  onDrop, 
  onDragOver, 
  onPaste,
  disabled = false,
  placeholder = "Type your message...",
  selectedModel = '',
  supportsVision = false,
  onInputChange,
  tokenStats,
  sessionTracker,
  includeCurrentFiles = false,
  onToggleIncludeFiles = null,
  sidebarOpen = false,
  hideInputArea = false
}) => {
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [selectedModelData, setSelectedModelData] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Remove auto-scroll - handled by parent AIChat component
  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages]);
  
  // Monitor session status and selected model
  useEffect(() => {
    const updateSessionStatus = () => {
      const stats = window.sessionStats || {};
      setSessionBlocked(stats.isBlocked || false);
    };
    
    const handleSessionUpdate = () => updateSessionStatus();
    window.addEventListener('sessionStatsUpdated', handleSessionUpdate);
    updateSessionStatus();
    
    return () => {
      window.removeEventListener('sessionStatsUpdated', handleSessionUpdate);
    };
  }, []);
  
  // Get selected model data from cache
  useEffect(() => {
    const getModelData = async () => {
      if (selectedModel && window.modelsCache) {
        const modelData = window.modelsCache.models?.find(m => m.id === selectedModel);
        setSelectedModelData(modelData || null);
      }
    };
    getModelData();
  }, [selectedModel]);
  
  // Helper function to get accepted file types
  const getAcceptedFileTypes = () => {
    const acceptedTypes = [];
    
    const modelsCache = window.modelsCache;
    if (modelsCache && selectedModel) {
      const model = modelsCache.models?.find(m => m.id === selectedModel);
      if (model && model.acceptedFiles) {
        if (model.acceptedFiles.includes('image')) acceptedTypes.push('image/*');
        if (model.acceptedFiles.includes('pdf')) acceptedTypes.push('.pdf');
        if (model.acceptedFiles.includes('docx')) acceptedTypes.push('.doc,.docx');
        if (model.acceptedFiles.includes('txt')) acceptedTypes.push('.txt');
        if (model.acceptedFiles.includes('csv')) acceptedTypes.push('.csv');
      }
    }
    
    if (acceptedTypes.length === 0) {
      if (supportsVision) acceptedTypes.push('image/*');
      if (tokenStats?.acceptedFiles?.includes('pdf')) acceptedTypes.push('.pdf');
      if (tokenStats?.acceptedFiles?.includes('docx')) acceptedTypes.push('.doc,.docx');
      if (tokenStats?.acceptedFiles?.includes('txt')) acceptedTypes.push('.txt');
      if (tokenStats?.acceptedFiles?.includes('csv')) acceptedTypes.push('.csv');
    }
    
    return acceptedTypes.length > 0 ? acceptedTypes.join(',') : '.txt';
  };

  // Helper function to get file upload tooltip
  const getFileUploadTooltip = () => {
    const modelsCache = window.modelsCache;
    if (modelsCache && selectedModel) {
      const model = modelsCache.models?.find(m => m.id === selectedModel);
      if (model && model.acceptedFiles) {
        const types = model.acceptedFiles.join(', ');
        return `Upload files: ${types}`;
      }
    }
    return supportsVision ? "Upload files or images" : "Upload text files only (this model doesn't support images)";
  };
  
  const getInputPlaceholder = () => {
    if (sessionBlocked && selectedModelData && !selectedModelData.free) {
      return "Please select a free model to continue";
    }
    if (sessionBlocked && (!selectedModelData || selectedModelData.free)) {
      return "Free model selected - you can continue chatting";
    }
    return placeholder;
  };
  
  const getInputDisabled = () => {
    if (disabled) return true;
    if (sessionBlocked && selectedModelData && !selectedModelData.free) return true;
    return false;
  };
  
  const getSendDisabled = () => {
    if (!inputMessage.trim()) return true;
    if (disabled) return true;
    if (sessionBlocked && selectedModelData && !selectedModelData.free) return true;
    return false;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // === NEW: Wrap send with session logic ===
  const handleSend = () => {
    if (getSendDisabled()) return;

    // Allow free models to proceed even during session limits
    if (sessionBlocked && selectedModelData && selectedModelData.free) {
      console.log('Allowing free model during session limit');
      onSendMessage();
      return;
    }

    // For paid models, check cost and balance
    if (selectedModelData && !selectedModelData.free) {
      const estimatedCost = tokenStats?.nextMessageCost || 0;
      
      // Get user balance from the session stats or window
      const userBalance = window.sessionStats?.balance || 0;
      
      if (userBalance < estimatedCost) {
        console.log('Send blocked: Insufficient balance');
        alert('Insufficient balance for this request. Please use a free model or add more balance.');
        return;
      }
      
      // Show cost confirmation for expensive messages (>$0.50)
      if (estimatedCost > 0.5) {
        setShowCostModal(true);
        setPendingMessage(true);
        return;
      }
    }

    // Don't block based on session tokens for now - let server handle it
    onSendMessage();
  };

  const handleCostConfirm = () => {
    setShowCostModal(false);
    setPendingMessage(null);
    onSendMessage();
  };

  const handleCostCancel = () => {
    setShowCostModal(false);
    setPendingMessage(null);
  };

  return (
    <div 
      className="chat-container"
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      <div className={`messages-area ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.role}`}>
            <div className="message-icon">
              {message.role === 'user' ? <User size={20} /> : <Bot size={20} />}
            </div>
            <div className="message-content">
              <MessageRenderer 
                content={message.content} 
                isError={message.isError}
                role={message.role}
                isSystemMessage={message.isSystemMessage}
                attachments={message.attachments || []}
              />
              {}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message assistant">
            <div className="message-icon">
              <Bot size={20} />
            </div>
            <div className="message-content">
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {!hideInputArea && (
        <div className={`input-area ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {attachments.length > 0 && (
            <div className="attachments-preview">
              {attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  {attachment.type === 'image' ? (
                    <img 
                      src={attachment.data} 
                      alt={attachment.name}
                      className="attachment-thumb"
                    />
                  ) : (
                    <div className="file-attachment">
                      <Paperclip size={16} />
                      <span>{attachment.name}</span>
                    </div>
                  )}
                  <button 
                    className="remove-attachment"
                    onClick={() => onRemoveAttachment(index)}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <TokenDisplay tokenStats={tokenStats} />
          
          {onToggleIncludeFiles && (
            <div className="include-files-checkbox" hidden>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeCurrentFiles || false}
                  onChange={(e) => onToggleIncludeFiles(e.target.checked)}
                  className="checkbox-input"
                />
                <span className="checkbox-text">
                  Include all project files in context
                  {includeCurrentFiles && <span className="files-loaded"> ✓ Loaded</span>}
                  {!includeCurrentFiles && <span className="files-not-loaded"> (Click to load)</span>}
                </span>
              </label>
            </div>
          )}
          
          <div className="action-buttons">
            <button 
              hidden
              className="vibe-action-btn layout-btn"
              onClick={() => {
                if (window.toggleProjectSelector) {
                  window.toggleProjectSelector();
                }
              }}
              disabled={disabled}
              title="Select Project"
            >
              📁 Project
            </button>
            <button 
              hidden
              className="vibe-action-btn layout-btn"
              onClick={() => {
                if (window.toggleFileExplorer) {
                  window.toggleFileExplorer();
                }
              }}
              disabled={disabled}
              title="File Explorer"
            >
              <Code2 size={20} />
            </button>
          </div>

          <div className="input-row">
            {onFileUpload && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={onFileUpload}
                  multiple
                  accept={getAcceptedFileTypes()}
                  style={{ display: 'none' }}
                />
                <button 
                  className="attachment-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title={getFileUploadTooltip()}
                >
                  <Paperclip size={20} />
                </button>
              </>
            )}
            
            <textarea
              value={inputMessage}
              onChange={(e) => {
                const value = e.target.value;
                setInputMessage(value);
                if (onInputChange) {
                  onInputChange(value);
                }
              }}
              placeholder={getInputPlaceholder()}
              onKeyPress={handleKeyPress}
              onPaste={onPaste}
              className="message-input"
              rows="1"
              disabled={getInputDisabled()}
            />
            
            <button 
              onClick={handleSend}
              disabled={getSendDisabled()}
              className="send-btn"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
      
      {showCostModal && (
        <CostConfirmationModal
          cost={tokenStats?.nextMessageCost || 0}
          onConfirm={handleCostConfirm}
          onCancel={handleCostCancel}
        />
      )}
    </div>
  );
};

export default ChatContainer;